import { useSeo } from "../hooks/useSeo";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { useRevealAll } from "../hooks/useReveal";

const LAST_UPDATED = "2026. február";

export default function PrivacyPolicy() {
  useSeo({
    title: "Adatvédelmi tájékoztató | Hair Elixir Hungary",
    description: "A Hair Elixir Hungary adatvédelmi tájékoztatója.",
    path: "/adatvedelem",
  });
  const ref = useRef(null);
  useRevealAll(ref);

  return (
    <main
      ref={ref}
      data-testid="page-privacy"
      className="pt-28 md:pt-32 bg-[color:var(--he-bg)]"
    >
      {/* Breadcrumb */}
      <div className="he-container py-8 flex items-center justify-between">
        <Link to="/" className="btn-ghost" data-testid="privacy-back">
          <ChevronLeft size={14} className="ghost-arrow" />
          <span>Vissza a kezdőlapra</span>
        </Link>
        <span className="overline-xs text-[color:var(--he-ink-soft)] hidden sm:inline">
          Utolsó frissítés · {LAST_UPDATED}
        </span>
      </div>

      {/* Header */}
      <section className="he-container pt-8 pb-16 md:pb-24 border-b border-[color:var(--he-line)]">
        <p className="overline mb-5 mag-marker reveal">
          <span>Jogi tájékoztató</span>
        </p>
        <h1
          className="font-serif-display tracking-[-0.022em] reveal delay-1"
          style={{
            fontWeight: 400,
            fontSize: "clamp(2.6rem, 6vw, 5.2rem)",
            lineHeight: 1.05,
          }}
        >
          Adatvédelmi <span className="italic text-[color:var(--he-gold)]">tájékoztató.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[color:var(--he-ink-soft)] font-light leading-[1.85] reveal delay-2">
          Ez a tájékoztató az általunk kezelt személyes adatokról, a kezelés
          céljáról, jogalapjáról és időtartamáról, valamint az Ön ehhez fűződő
          jogairól szól. A GDPR (2016/679 EU rendelet) és az Info tv. (2011.
          évi CXII.) alapján készült.
        </p>
      </section>

      {/* Body */}
      <section className="he-container py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        {/* TOC — sticky on desktop */}
        <aside className="md:col-span-3 order-2 md:order-1">
          <div className="md:sticky md:top-32">
            <p className="overline-xs text-[color:var(--he-gold)] mb-5">Tartalomjegyzék</p>
            <ol className="space-y-3 text-sm font-light">
              {[
                ["#adatkezelo", "1. Adatkezelő"],
                ["#adatok", "2. Kezelt adatok"],
                ["#cel-jogalap", "3. Cél és jogalap"],
                ["#megorzes", "4. Megőrzés"],
                ["#feldolgozok", "5. Adatfeldolgozók"],
                ["#jogok", "6. Az Ön jogai"],
                ["#biztonsag", "7. Adatbiztonság"],
                ["#panasz", "8. Panasz és jogorvoslat"],
                ["#modositas", "9. Módosítás"],
              ].map(([href, label]) => (
                <li key={href}>
                  <a href={href} className="he-link text-[color:var(--he-ink-soft)]">
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        {/* Article */}
        <article className="md:col-span-9 order-1 md:order-2 space-y-14 reveal">
          <Section id="adatkezelo" title="1. Az adatkezelő">
            <Kv label="Név" value="Dócziné Bakró Hajnalka egyéni vállalkozó" />
            <Kv label="Márkanév" value="Hair Elixir" />
            <Kv label="Adószám" value="91672315-1-23" />
            <Kv label="Főtevékenység" value="TEÁOR 9621 — Fodrászat, borbélyszolgáltatás" />
            <Kv label="E-mail" value="hairelixirhungary@gmail.com" href="mailto:hairelixirhungary@gmail.com" />
            <p className="mt-6 text-[color:var(--he-ink-soft)] font-light leading-[1.85]">
              Adatvédelmi tisztviselő kijelölésére nem vagyunk kötelezettek. Az
              adatkezeléssel kapcsolatos megkeresések a fenti e-mail címre
              küldhetők; érdemi választ 30 napon belül adunk.
            </p>
          </Section>

          <Section id="adatok" title="2. Milyen személyes adatokat kezelünk">
            <p>
              A megrendelés leadásához és teljesítéséhez a következő adatokat
              kezeljük:
            </p>
            <Bullets
              items={[
                "Vezeték- és keresztnév",
                "E-mail cím",
                "Telefonszám (kiszállítási egyeztetéshez)",
                "Számlázási és szállítási cím",
                "Rendelés tartalma, összege, a fizetéshez kapcsolódó azonosítók (Stripe fizetési azonosító, számla sorszám)",
                "Az általunk küldött e-mailekre adott válaszok, ügyfélszolgálati kommunikáció",
              ]}
            />
            <p className="mt-6">
              Bankkártya-adatot <strong>nem tárolunk és nem is látunk</strong>. A
              fizetést a Stripe Payments Europe, Ltd. dolgozza fel a saját,
              PCI-DSS szintű rendszerében; mi csak a tranzakció eredményét és
              azonosítóját kapjuk vissza.
            </p>
          </Section>

          <Section id="cel-jogalap" title="3. Az adatkezelés célja és jogalapja">
            <Table
              rows={[
                ["Megrendelés teljesítése, kézbesítés, számlázás", "Szerződés teljesítése — GDPR 6. cikk (1) b)"],
                ["Számviteli megőrzés (bizonylat, számla)", "Jogi kötelezettség — GDPR 6. cikk (1) c) + 2000. évi C. tv. 169. §"],
                ["Ügyfélszolgálati kommunikáció, panaszkezelés", "Szerződés teljesítése + jogi kötelezettség (fogyasztóvédelmi tv.)"],
                ["Hírlevél küldése (feliratkozás esetén)", "Az érintett önkéntes hozzájárulása — GDPR 6. cikk (1) a). Bármikor visszavonható."],
                ["Csalásmegelőzés a fizetési folyamatban", "Jogos érdek (mi és a Stripe) — GDPR 6. cikk (1) f)"],
              ]}
            />
          </Section>

          <Section id="megorzes" title="4. Meddig őrizzük az adatokat">
            <Bullets
              items={[
                "Számviteli bizonylatok (számla, rendelési adatok, fizetési azonosító): a Szt. 169. § alapján 8 év",
                "Ügyfélszolgálati e-mailek: a rendelést követő 5 év (jogos érdek — igényérvényesítés)",
                "Hírlevél feliratkozás: leiratkozásig; ezt követően a hozzájárulást igazoló minimális log megőrzési kötelezettségig",
                "A weboldalról leadott, de nem véglegesített kosár: 30 nap után törlésre kerül",
              ]}
            />
          </Section>

          <Section id="feldolgozok" title="5. Kik férhetnek hozzá az adatokhoz (adatfeldolgozók, címzettek)">
            <p>
              Az adatokat a jogszabály vagy szerződés által feljogosított
              partnereink kezelik a nevünkben. Aktuális partnereink:
            </p>
            <Table
              rows={[
                ["Stripe Payments Europe, Ltd. (Írország, EGT)", "Bankkártyás fizetés feldolgozása, csalásmegelőzés"],
                ["Google (Gmail SMTP)", "Rendelés-visszaigazoló és ügyfélszolgálati e-mailek küldése"],
                ["Emergent Agent, Inc. (hosting)", "A weboldal és az alkalmazás üzemeltetése, biztonsági mentés"],
                ["Futárszolgálat (a megrendelt szállítási módtól függően)", "Csomagkézbesítés"],
                ["Számlázó szolgáltató", "Elektronikus számla kiállítása"],
              ]}
            />
            <p className="mt-6">
              Harmadik országba (EGT-n kívülre) személyes adatot csak akkor
              továbbítunk, ha a szolgáltató megfelelő garanciákat nyújt (pl. EU
              Standard Contractual Clauses). Marketing célból harmadik félnek
              nem adjuk át az adatait.
            </p>
          </Section>

          <Section id="jogok" title="6. Az Ön jogai">
            <p>Ön az alábbi jogokkal élhet a személyes adataira vonatkozóan:</p>
            <Bullets
              items={[
                "Tájékoztatáshoz és hozzáféréshez való jog — kérheti, milyen adatait, milyen célból kezeljük",
                "Helyesbítéshez való jog — pontatlan adat javítását bármikor kérheti",
                "Törléshez való jog („elfeledtetés”) — a törvényi megőrzés lejártáig terjedő korlátozással",
                "Az adatkezelés korlátozásához való jog",
                "Adathordozhatósághoz való jog — az Ön által megadott adatokat géppel olvasható formátumban átadjuk",
                "Tiltakozáshoz való jog — a jogos érdeken alapuló adatkezeléssel szemben",
                "Hozzájárulás visszavonása — pl. hírlevél leiratkozás egyetlen kattintással",
              ]}
            />
            <p className="mt-6">
              Kérését a{" "}
              <a href="mailto:hairelixirhungary@gmail.com" className="he-link">
                hairelixirhungary@gmail.com
              </a>{" "}
              címen tudja jelezni. A választ költségmentesen, 30 napon belül
              megadjuk. Kivételes esetben ez a határidő legfeljebb két hónappal
              meghosszabbítható, amelyről Önt tájékoztatjuk.
            </p>
          </Section>

          <Section id="biztonsag" title="7. Adatbiztonság">
            <p>
              A weboldal HTTPS titkosított kapcsolaton keresztül működik. Az
              ügyféladatokat hozzáférés-korlátozott rendszerben tároljuk,
              rendszeres biztonsági mentés mellett. A fizetési adatok a Stripe
              zárt, PCI-DSS Level 1 minősítésű rendszerében kerülnek
              feldolgozásra — mi bankkártya-adatokat nem tárolunk és nem is
              látunk.
            </p>
          </Section>

          <Section id="panasz" title="8. Panasz és jogorvoslat">
            <p>
              Ha úgy érzi, hogy a személyes adatainak kezelésével kapcsolatban
              jogsérelem érte, kérjük, először közvetlenül nálunk jelezze —
              törekszünk minden ügyet gyorsan és korrektül rendezni.
            </p>
            <p>
              Emellett panasszal fordulhat a felügyeleti hatósághoz, illetve
              bírósághoz is:
            </p>
            <Kv
              label="Hatóság"
              value="Nemzeti Adatvédelmi és Információszabadság Hatóság (NAIH)"
            />
            <Kv label="Cím" value="1055 Budapest, Falk Miksa utca 9-11." />
            <Kv label="Postacím" value="1363 Budapest, Pf. 9." />
            <Kv label="Telefon" value="+36 (1) 391-1400" />
            <Kv
              label="Honlap"
              value="naih.hu"
              href="https://www.naih.hu"
            />
          </Section>

          <Section id="modositas" title="9. A tájékoztató módosítása">
            <p>
              Fenntartjuk a jogot, hogy jelen tájékoztatót időről időre — a
              jogszabályi vagy szolgáltatásbeli változásoknak megfelelően —
              módosítsuk. A módosított változat a közzététel napjától hatályos;
              lényeges változás esetén a rendelést leadó ügyfeleket e-mailben
              is értesítjük.
            </p>
            <p className="mt-6 text-[color:var(--he-ink-soft)] font-light">
              Jelen dokumentum hatályos: <strong>{LAST_UPDATED}</strong>.
            </p>
          </Section>

          <div className="pt-10 border-t border-[color:var(--he-line)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <Link to="/cookie-tajekoztato" className="btn-ghost" data-testid="privacy-to-cookie">
              <span>Cookie-tájékoztató</span>
              <ArrowUpRight size={14} className="ghost-arrow" />
            </Link>
            <Link to="/kapcsolat" className="btn-outline-gold" data-testid="privacy-contact">
              <span>Kérdésed van? Írj nekünk</span>
            </Link>
          </div>
        </article>
      </section>
    </main>
  );
}

/* ---------- helpers ---------- */

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-32 reveal">
      <h2
        className="font-serif-display tracking-[-0.018em] mb-6"
        style={{ fontWeight: 400, fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)", lineHeight: 1.15 }}
      >
        {title}
      </h2>
      <div className="space-y-4 text-[color:var(--he-ink)] font-light leading-[1.85]">
        {children}
      </div>
    </section>
  );
}

function Kv({ label, value, href }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-2.5 border-b border-[color:var(--he-line)]">
      <span className="overline-xs text-[color:var(--he-ink-soft)] shrink-0 sm:min-w-[140px]">
        {label}
      </span>
      {href ? (
        <a href={href} className="he-link text-[color:var(--he-ink)]" target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
          {value}
        </a>
      ) : (
        <span className="text-[color:var(--he-ink)]">{value}</span>
      )}
    </div>
  );
}

function Bullets({ items }) {
  return (
    <ul className="space-y-2.5 mt-3">
      {items.map((it, i) => (
        <li key={i} className="pl-5 relative">
          <span
            className="absolute left-0 top-[0.85em] w-2 h-px bg-[color:var(--he-gold)]"
            aria-hidden
          />
          {it}
        </li>
      ))}
    </ul>
  );
}

function Table({ rows }) {
  return (
    <div className="mt-5 border-t border-[color:var(--he-line)]">
      {rows.map(([left, right], i) => (
        <div
          key={i}
          className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-8 py-4 border-b border-[color:var(--he-line)]"
        >
          <p className="text-[color:var(--he-ink)]">{left}</p>
          <p className="text-[color:var(--he-ink-soft)] md:text-right">{right}</p>
        </div>
      ))}
    </div>
  );
}
